import React, { useEffect, useRef, useState } from 'react';

import { Box, Button, TextField, Typography } from '@mui/material';

import { AdminPageHeader } from '../../components/admin/AdminPageHeader';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { userReducer } from '../../store/reducers/user.reducer';
import { getCopiedProject, getIsCopyingProject, getUser } from '../../store/selectors/user.selector';
import { history } from '../../store/store';
import { CopiedProject } from '../../types/user';

const { clearCopiedProject, copyProject } = userReducer.actions;

export const CopyProjectPage = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const isCopyingProject = useAppSelector(getIsCopyingProject);
  const copiedProject = useAppSelector(getCopiedProject);
  const awaitingResult = useRef(false);
  const [createdProject, setCreatedProject] = useState<CopiedProject | undefined>();
  const [projectId, setProjectId] = useState('');
  const [targetAccountId, setTargetAccountId] = useState('');
  const [projectIdError, setProjectIdError] = useState('');
  const [targetAccountIdError, setTargetAccountIdError] = useState('');

  useEffect(() => {
    if (user && !user.roles.includes('ADMIN')) {
      history.push('/forbidden');
    }
  }, [user]);

  useEffect(() => {
    dispatch(clearCopiedProject());
    return () => {
      dispatch(clearCopiedProject());
    };
  }, [dispatch]);

  useEffect(() => {
    if (awaitingResult.current && copiedProject) {
      setCreatedProject(copiedProject);
      awaitingResult.current = false;
    }
  }, [copiedProject]);

  const validateId = (value: string, setError: (message: string) => void, label: string) => {
    if (!/^[1-9]\d*$/.test(value.trim())) {
      setError(`${label} must be a positive number`);
      return false;
    }

    setError('');
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const isProjectIdValid = validateId(projectId, setProjectIdError, 'Project ID');
    const isTargetAccountIdValid = validateId(targetAccountId, setTargetAccountIdError, 'Target account ID');

    if (!isProjectIdValid || !isTargetAccountIdValid) {
      return;
    }

    awaitingResult.current = true;
    setCreatedProject(undefined);
    dispatch(
      copyProject({
        projectId: Number(projectId),
        targetAccountId: Number(targetAccountId),
      }),
    );
  };

  if (!user || !user.roles.includes('ADMIN')) {
    return null;
  }

  return (
    <Box>
      <AdminPageHeader
        title="Copy Project"
        subtitle="Copy a project onto another account. The original project stays where it is. The copy keeps the project name and is placed in the target account's root."
      />
      <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 480 }}>
        <TextField
          margin="normal"
          required
          fullWidth
          id="projectId"
          label="Project ID"
          name="projectId"
          type="text"
          inputMode="numeric"
          autoFocus
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          error={!!projectIdError}
          helperText={projectIdError}
          disabled={isCopyingProject}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          id="targetAccountId"
          label="Target account ID"
          name="targetAccountId"
          type="text"
          inputMode="numeric"
          value={targetAccountId}
          onChange={(e) => setTargetAccountId(e.target.value)}
          error={!!targetAccountIdError}
          helperText={targetAccountIdError || 'The user ID of the account that should receive the copy'}
          disabled={isCopyingProject}
        />
        <Button type="submit" variant="contained" sx={{ mt: 3 }} disabled={isCopyingProject}>
          {isCopyingProject ? 'Copying...' : 'Copy Project'}
        </Button>
        {createdProject && (
          <Typography sx={{ mt: 2 }}>
            Created project {createdProject.id} ({createdProject.name}) on the target account.
          </Typography>
        )}
      </Box>
    </Box>
  );
};
